/**
 * useAddCirurgiaForm - Form state and validation logic for AddCirurgiaModal
 */
import { useState, useEffect, useCallback } from 'react';
import type { Cliente } from '../services/clienteService';

export interface AddCirurgiaFormData {
  cliente_id: number;
  com_regime: boolean | null;
  data_hora: string;
  paciente: string;
  medico: string;
  convenio: string;
  codigo_produto: string;
  descricao_produto?: string;
  lote: string;
  quantidade: number;
  cnpj: string;
}

interface UseAddCirurgiaFormOptions {
  isOpen: boolean;
  jobClientes: Cliente[];
  onClienteChanged?: (clienteId: number) => void;
}

function isValidDate(dateStr: string): boolean {
  if (!dateStr || dateStr.length !== 10) return false;
  const parts = dateStr.split('/');
  if (parts.length !== 3) return false;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  if (year < 2000 || year > 2100) return false;
  return true;
}

function isValidTime(timeStr: string): boolean {
  if (!timeStr || timeStr.length !== 5) return false;
  const parts = timeStr.split(':');
  if (parts.length !== 2) return false;
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (isNaN(hours) || isNaN(minutes)) return false;
  if (hours < 0 || hours > 23) return false;
  if (minutes < 0 || minutes > 59) return false;
  return true;
}

export function useAddCirurgiaForm({ isOpen, jobClientes, onClienteChanged }: UseAddCirurgiaFormOptions) {
  const getDefaultFormData = (clientes: Cliente[]): AddCirurgiaFormData => {
    const defaultClienteId = clientes.length === 1 ? clientes[0].id : 0;
    const defaultCliente = clientes.length === 1 ? clientes[0] : null;
    return {
      cliente_id: defaultClienteId,
      com_regime: null,
      data_hora: '',
      paciente: '',
      medico: '',
      convenio: '',
      codigo_produto: '',
      descricao_produto: '',
      lote: '',
      quantidade: 1,
      cnpj: defaultCliente?.cnpj || '',
    };
  };

  const [formData, setFormData] = useState<AddCirurgiaFormData>(() => getDefaultFormData(jobClientes));
  const [error, setError] = useState<string | null>(null);
  const [showRegimeSelector, setShowRegimeSelector] = useState(false);
  const [dateInput, setDateInput] = useState('');
  const [timeInput, setTimeInput] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const defaultClienteId = jobClientes.length === 1 ? jobClientes[0].id : 0;
      const defaultCliente = jobClientes.length === 1 ? jobClientes[0] : null;
      const newFormData = getDefaultFormData(jobClientes);
      setFormData(newFormData);
      setError(null);
      setDateInput('');
      setTimeInput('');

      if (defaultCliente) {
        if (defaultCliente.com_regime && defaultCliente.sem_regime) {
          setShowRegimeSelector(true);
        } else {
          setShowRegimeSelector(false);
          setFormData(prev => ({ ...prev, com_regime: defaultCliente.com_regime ? true : false }));
        }
        onClienteChanged?.(defaultClienteId);
      }
    }
  }, [isOpen, jobClientes]);

  const updateDateTime = useCallback((date: string, time: string) => {
    if (date.length === 10 && time.length === 5) {
      const parts = date.split('/');
      const isoDateTime = `${parts[2]}-${parts[1]}-${parts[0]}T${time}:00`;
      setFormData(prev => ({ ...prev, data_hora: isoDateTime }));
    } else {
      setFormData(prev => ({ ...prev, data_hora: '' }));
    }
  }, []);

  const handleDateChange = useCallback((value: string) => {
    const numbers = value.replace(/\D/g, '');
    let formatted = '';
    if (numbers.length > 0) {
      formatted = numbers.slice(0, 2);
      if (numbers.length > 2) formatted += '/' + numbers.slice(2, 4);
      if (numbers.length > 4) formatted += '/' + numbers.slice(4, 8);
    }
    setDateInput(formatted);
    updateDateTime(formatted, timeInput);
  }, [timeInput, updateDateTime]);

  const handleTimeChange = useCallback((value: string) => {
    const numbers = value.replace(/\D/g, '');
    let formatted = '';
    if (numbers.length > 0) {
      formatted = numbers.slice(0, 2);
      if (numbers.length > 2) formatted += ':' + numbers.slice(2, 4);
    }
    setTimeInput(formatted);
    updateDateTime(dateInput, formatted);
  }, [dateInput, updateDateTime]);

  const handleClienteChange = useCallback((clienteId: number) => {
    const cliente = jobClientes.find(c => c.id === clienteId);
    let comRegime: boolean | null = null;
    let needsRegimeSelector = false;
    if (cliente) {
      if (cliente.com_regime && !cliente.sem_regime) comRegime = true;
      else if (!cliente.com_regime && cliente.sem_regime) comRegime = false;
      else if (cliente.com_regime && cliente.sem_regime) needsRegimeSelector = true;
    }
    setFormData(prev => ({
      ...prev,
      cliente_id: clienteId,
      com_regime: comRegime,
      cnpj: cliente?.cnpj || '',
      codigo_produto: '',
      descricao_produto: '',
    }));
    setShowRegimeSelector(needsRegimeSelector);
    onClienteChanged?.(clienteId);
  }, [jobClientes, onClienteChanged]);

  const setProductFields = useCallback((codigo: string, descricao: string) => {
    setFormData(prev => ({ ...prev, codigo_produto: codigo, descricao_produto: descricao }));
  }, []);

  const validate = useCallback((): string | null => {
    if (formData.cliente_id === 0) return 'Selecione um cliente';
    if (showRegimeSelector && formData.com_regime === null) return 'Selecione o regime tributario';
    if (!isValidDate(dateInput)) return 'Data invalida. Use o formato DD/MM/AAAA';
    if (!isValidTime(timeInput)) return 'Hora invalida. Use o formato HH:MM';
    if (!formData.data_hora) return 'Data/Hora e obrigatoria';
    if (!formData.paciente.trim()) return 'Nome do paciente e obrigatorio';
    if (!formData.medico.trim()) return 'Nome do medico e obrigatorio';
    if (!formData.convenio.trim()) return 'Convenio e obrigatorio';
    if (!formData.codigo_produto.trim()) return 'Selecione um produto da lista';
    if (!formData.lote.trim()) return 'Lote e obrigatorio';
    if (formData.quantidade <= 0) return 'Quantidade deve ser maior que zero';
    if (!formData.cnpj.trim()) return 'CNPJ e obrigatorio';
    return null;
  }, [formData, showRegimeSelector, dateInput, timeInput]);

  return {
    formData,
    setFormData,
    error,
    setError,
    showRegimeSelector,
    dateInput,
    timeInput,
    handleDateChange,
    handleTimeChange,
    handleClienteChange,
    setProductFields,
    validate,
  };
}
